import { httpClient } from '@/shared/api/httpClient'

import type { IChainDetails, IChainsList } from '@/modules/matrix/interfaces/chains.interface'
import type { IExposePartnerParams } from '@/modules/matrix/interfaces/partners.interface'
import type {
  IBalance,
  IBuyBoosterParams,
  IMatrix,
  ListOfTypes,
} from '@/modules/matrix/interfaces/store.interface'
import type { DirectPaymentResponse } from '@/modules/payment/types/directPayment'

/**
 * Payload for matrix activation (direct purchase).
 * Optional fields are DirectPaymentService routing hints (wallet | onchainpay | external).
 */
export interface MatrixActivationPayload {
  type: string
  payment_method?: string
  currency?: string
  crypto_network?: string
  crypto_token?: string
}

/**
 * Payload for matrix BOOST (direct purchase + placement into a parent cell).
 */
export interface MatrixBoostPayload {
  matrix_id: number
  depth: number
  pos: number
  payment_method?: string
  currency?: string
  crypto_network?: string
  crypto_token?: string
}

/**
 * Payload for a BASKET purchase — several flows activated by a single order (D-27).
 */
export interface MatrixBasketPayload {
  types: string[]
  payment_method?: string
  currency?: string
  crypto_network?: string
  crypto_token?: string
  client_request_id?: string
}

/**
 * Matrix API wrapper built on the GonkaGame shell `httpClient`.
 *
 * The shell client base URL is `/api`, so endpoints omit that prefix. Every
 * method resolves to the parsed backend envelope `{ data, error_code? }`. The
 * legacy axios + cookie auth (`api/config.ts`) is dropped entirely — auth,
 * retries and error toasts are handled by `httpClient`.
 */

export interface ApiEnvelope<T> {
  data: T
  error_code?: number
  error_message?: string
}

export interface IPartnersPage {
  currentPage: number
  list: unknown[]
  totalCount: number
  totalPages: number
}

type MatrixViewData = IMatrix & { tabs?: ListOfTypes }

// --- localStorage cache helpers (no auth/cookie logic) ---

export function setDataToLS(key: string, data: unknown): void {
  localStorage.setItem(key, JSON.stringify(data))
}

export function getDataFromLS<T = unknown>(key: string): T | null {
  const raw = localStorage.getItem(key)
  return raw && raw !== 'undefined' ? (JSON.parse(raw) as T) : null
}

// --- GET ---

export function getListOfTypes(category: string) {
  return httpClient.get<ApiEnvelope<ListOfTypes>>(`/matrix/list-of-types/${category}`)
}

export function getMatrix(matrixTypeOrId: string | number, options?: { signal?: AbortSignal }) {
  return httpClient.get<ApiEnvelope<MatrixViewData>>(`/matrix/${matrixTypeOrId}`, options)
}

export function getMatrixByUUID(matrixUUID: string, options?: { signal?: AbortSignal }) {
  return httpClient.get<ApiEnvelope<MatrixViewData>>(`/matrix/uuid/${matrixUUID}`, options)
}

export function getBalance() {
  return httpClient.get<ApiEnvelope<IBalance>>('/wallet/balance')
}

export function getListOfChains(pageID: number) {
  return httpClient.get<ApiEnvelope<IChainsList>>(`/matrix/chains/profit?page=${pageID}`)
}

export function getChainDetail(uuid: string) {
  return httpClient.get<ApiEnvelope<IChainDetails>>(`/matrix/chain-details/${uuid}`)
}

export function getListOfInfinity(parentId: number) {
  return httpClient.get<ApiEnvelope<{ list: unknown[] }>>(`/matrix/list-of-infinity/${parentId}`)
}

// --- POST ---

export interface IFilterParams {
  matrixType: string
  matrixFilterUserId: number
  matrixFilterPageId: number
  filter: Record<string, unknown>
  matrixUUID?: string | number
}

export function filterOfActivatedMatrix({
  matrixType,
  matrixFilterUserId,
  matrixFilterPageId,
  filter,
  matrixUUID,
}: IFilterParams, options?: { signal?: AbortSignal }) {
  const matrixIdParam = matrixUUID ? `/${matrixUUID}` : ''

  return httpClient.post<ApiEnvelope<IPartnersPage>>(
    `/matrix/get-clones/${matrixType}/${matrixFilterUserId}/${matrixFilterPageId}${matrixIdParam}`,
    { filter },
    options,
  )
}

export function placementClone(data: IBuyBoosterParams) {
  return httpClient.post<ApiEnvelope<unknown>>('/matrix/placement-clone', data)
}

/**
 * Activate a matrix (direct purchase) via the unified DirectPaymentService.
 * Returns the discriminated union `paid | pending | redirect`.
 */
export function payMatrixActivation(payload: MatrixActivationPayload) {
  return httpClient.post<DirectPaymentResponse>('/matrix/direct-payment/activate', payload)
}

/**
 * Оплата нескольких потоков одним заказом (D-27).
 * Returns the same discriminated union as the single activation.
 */
export function payMatrixBasket(payload: MatrixBasketPayload) {
  return httpClient.post<DirectPaymentResponse>('/matrix/direct-payment/activate-basket', payload)
}

/**
 * Buy a BOOST into a specific parent cell via the unified DirectPaymentService.
 * Returns the discriminated union `paid | pending | redirect`.
 */
export function payMatrixBoost(payload: MatrixBoostPayload) {
  return httpClient.post<DirectPaymentResponse>('/matrix/direct-payment/boost', payload)
}

// --- PUT ---

export function placementExistingMatrix(data: IExposePartnerParams) {
  return httpClient.put<ApiEnvelope<unknown>>('/matrix/placement-existing-matrix', data)
}
